import React, { useState, useRef, useEffect } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { fetchInitialData, hasRecentInteractions } from '@fiverr-private/send_message';
import { object, bool, func } from 'prop-types';
import { isNil } from 'lodash';
import {
    reportSendMessageSubmitSuccess,
    redirectToInboxConversation
} from '../../../utils/contactSeller';
import { inExperiment } from '../../../utils/experiments';
import { PANTHERS_FMP_IMPROVEMENTS } from '../../../utils/experiments/experimentsEnum';

export const shouldUseContactSellerModal = () => {
    const { abTests } = getContext();

    return inExperiment(PANTHERS_FMP_IMPROVEMENTS, abTests);
};

export const FirstMessagePopup = (props, context) => {
    const firstMessagePackageRef = useRef();

    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState();

    useEffect(() => {
        const render = async() => {
            if (!isNil(modalData)) {
                openModal();
                return;
            }

            try {
                const [, data] = await Promise.all([lazyLoadModal(), fetchModalData()]);

                if (checkRecentInteractions(data)) {
                    redirect();
                    return;
                }

                openModal();
            } catch (error) {
                reportModalInitializationError(error);
                redirect();
            }
        };

        if (props.shouldRender) {
            render();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.shouldRender]);

    const lazyLoadModal = async() => {
        try {
            if (shouldUseContactSellerModal()) {
                firstMessagePackageRef.current = await import(/* webpackChunkName: 'ContactSellerPopup' */'./modals/contactSeller');
            } else {
                firstMessagePackageRef.current = await import(/* webpackChunkName: 'SendMessagePopup' */'./modals/sendMessage');
            }
        } catch (error) {
            reportLazyLoadError(error);
            throw error;
        }
    };

    const reportLazyLoadError = (error) => {
        context.logger.error(error, { description: 'Failed to lazy load @fiverr-private/send_message package.' });
    };

    const reportModalInitializationError = (error) => {
        context.logger.error(error, { description: 'Failed to initialize the First Message Popup' });
    };

    const checkRecentInteractions = (data) => {
        const sellerUsername = getSellerUsername();
        const { sender } = data;

        return hasRecentInteractions(sender.username, sellerUsername);
    };

    const redirect = () => {
        const { pathfinderWrapper } = context;
        redirectToInboxConversation(pathfinderWrapper);
    };

    const getSellerUsername = () => {
        const { pathParameters } = getContext();
        const { username: sellerUsername } = pathParameters;

        return sellerUsername;
    };

    const fetchModalData = async() => {
        if (!isNil(modalData)) {
            return modalData;
        }

        const sellerUsername = getSellerUsername();
        const data = await fetchInitialData(sellerUsername);

        setModalData(data);

        return data;
    };

    const openModal = () => {
        setShowModal(true);
    };

    const handleModalClose = () => {
        props.onClose();
        setShowModal(false);
    };

    const handleSubmitSuccess = () => {
        const { biEvents } = context;
        reportSendMessageSubmitSuccess(biEvents);
    };

    const getRelatedGigId = () => {
        const { general } = context;
        const { gigId } = general;

        return gigId;
    };

    const modalHasBeenLoaded = !isNil(firstMessagePackageRef.current);
    const Modal = modalHasBeenLoaded ? firstMessagePackageRef.current.default : EmptyComponent;
    const show = showModal && !isNil(modalData);

    return (
        <Modal show={show}
            onClose={handleModalClose}
            relatedGigId={getRelatedGigId()}
            onSubmitSuccess={handleSubmitSuccess}
            recipientInfo={modalData?.recipient}
            showInfo={true}/>
    );
};

const EmptyComponent = () => null;

FirstMessagePopup.propTypes = {
    shouldRender: bool,
    onClose: func
};

FirstMessagePopup.contextTypes = {
    pathfinderWrapper: object,
    general: object,
    logger: object,
    biEvents: object
};
